import React, { useEffect, useState } from "react";
import {
  Box,
  CssBaseline,
  Grid,
  Backdrop,
  CircularProgress,
} from "@mui/material";

import {
  drawerWidth,
  mainLayoutStyles,
  routes,
  showNotification,
} from "../../../../constants";
import Header from "../../../../common/Header";
import Sidebar from "../../../../common/Sidebar";
import {
  AddAreaModal,
  AreaManagementTable,
  AreaTabsPreview,
  FilterSection,
  SearchBar,
} from "../../components/components";
import FilterModalComponent from "../../../../common/FilterModalComponent";
import { useLocation, useNavigate } from "react-router-dom";
import { useAreasContext } from "../../../../providers/OrganizationAdmin/AreaProvider";
import {
  addAreaAtPosition,
  areaSearch,
  canViewDeletedAreas,
  filterNonDeletedAreas,
  findAreaNameConflict,
  normalizeQuickCollectSlug,
  softDeleteArea,
  updateArea,
} from "../../hooks/AreaHook";
import { Toast } from "../../../../common/alert";
import { useAuth } from "../../../../hooks";
import ConfirmationModal from "../../../../common/ConfirmationModal";
import { getSingleOrg } from "../../../OrgProf/hook/Organizationhook";

const AreaManagementContainer = () => {
  const { currentUser, organizationId } = useAuth();

  const { allAreas, setLoading, handleGetAllAreas, loading } =
    useAreasContext();

  const navigate = useNavigate();
  const [selected, setSelected] = useState([]);
  const [areaData, setAreaData] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const [addAreaModal, setAddAreaModal] = useState(false);
  const [addingArea, setAddingArea] = useState(false);
  const [searchActive, setSearchActive] = useState(false);

  // Org-level default for the Add Area modal's Status field (fallback "Active").
  const orgId = organizationId || currentUser?.organization || null;
  const [orgCreateAreaStatusDefault, setOrgCreateAreaStatusDefault] =
    useState("Active");

  // Refresh the org setting each time the modal opens so changes made on the
  // Org Profile page are reflected without a full reload.
  useEffect(() => {
    if (!orgId || !addAreaModal) return;
    let cancelled = false;
    (async () => {
      try {
        const orgSnap = await getSingleOrg(orgId);
        if (!cancelled && orgSnap?.exists?.()) {
          setOrgCreateAreaStatusDefault(
            orgSnap.data()?.createAreaStatusDefault === "Inactive"
              ? "Inactive"
              : "Active"
          );
        }
      } catch (error) {
        console.log("error loading org area status default:", error?.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, addAreaModal]);

  // Links to the old Add Area page redirect here with openAddArea set.
  const location = useLocation();
  useEffect(() => {
    if (location.state?.openAddArea) {
      setAddAreaModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);
  const [areaStatusFilterOptions, setAreaStatusFilterOptions] = useState([]);
  // True while the initial area list is being fetched. Used to show a
  // spinner / "Loading areas" message instead of the empty-state message.
  const [areasLoading, setAreasLoading] = useState(true);

  // console.log("areaData", areaData);
  useEffect(() => {
    let isMounted = true;
    setAreasLoading(true);
    Promise.resolve(handleGetAllAreas()).finally(() => {
      if (isMounted) setAreasLoading(false);
    });
    return () => {
      isMounted = false;
    };
    // if (allAreas?.length) {
    // } else {
    //   handleGetAllAreas();
    // }
  }, []);

  // Superadmins see every area (including soft-deleted ones) so they can
  // restore them. Everyone else only sees areas that are not deleted.
  // A missing isDeleted field is treated as false.
  const showDeleted = canViewDeletedAreas(currentUser);
  const permittedAreas = showDeleted
    ? allAreas || []
    : filterNonDeletedAreas(allAreas);

  // Area Status filter. An empty list means no filter ("All"), which is the
  // default. Selecting both Active and Inactive also shows every area.
  const visibleAreas =
    areaStatusFilterOptions.length > 0
      ? permittedAreas.filter((area) =>
          areaStatusFilterOptions.includes(
            area?.isActive ? "Active" : "Inactive"
          )
        )
      : permittedAreas;

  useEffect(() => {
    setAreaData(visibleAreas);
    setSelected([]);
  }, [allAreas, showDeleted, areaStatusFilterOptions]);

  const handleAddArea = async ({ name, isActive, position }) => {
    // Re-check uniqueness at submit time in case the list changed.
    if (findAreaNameConflict(allAreas, name)) {
      Toast("error", "An area with this name already exists.");
      return;
    }
    setAddingArea(true);
    try {
      const newAreaData = {
        name,
        isActive,
        isDeleted: false,
        createdAt: new Date(),
        quicktales: [],
        organization: organizationId
          ? organizationId
          : currentUser?.organization,
        createdBy: currentUser?.userName,
        // QuickCollect defaults: link is always live, approval is manual until
        // an admin opts in. See docs/QUICKCOLLECT_IMPLEMENTATION.md
        autoApprove: false,
        quickCollectSlug: normalizeQuickCollectSlug(name),
      };
      await addAreaAtPosition(newAreaData, position, allAreas);
      await handleGetAllAreas();
      setAddAreaModal(false);
      showNotification("Area added successfully.");
    } catch (error) {
      Toast("error", error.message);
      console.error("Error adding area:", error);
    } finally {
      setAddingArea(false);
    }
  };

  const handleFilterModalOpen = () => {
    setFilterModal(true);
  };
  const handleFilterModalClose = () => {
    setFilterModal(false);
  };
  const handleFilterAreas = async () => {
    // Filtering happens client-side in the effect above once the selected
    // options are stored; just close the drawer.
    handleFilterModalClose();
  };
  const resetFilter = () => {
    setAreaStatusFilterOptions([]);
  };
  const handleDeleteChip = (filterType, filterItem) => {
    if (filterType === "areaStatus") {
      setAreaStatusFilterOptions((prev) =>
        prev.filter((item) => item !== filterItem)
      );
    }
  };

  const handleDeleteModalOpen = () => {
    setDeleteModal(true);
  };
  const handleDeleteModalClose = () => {
    setDeleteModal(false);
  };

  const handleSearchAreaData = async (e) => {
    try {
      let value = e.target.value;
      setSearchActive(value.length >= 1);
      if (value.length >= 1) {
        let areaSearchTemp = await areaSearch(visibleAreas, value);
        setAreaData(areaSearchTemp);
      } else {
        setAreaData(visibleAreas);
      }
    } catch (error) {
      console.log("error searching album:", error.message);
    }
  };

  const handleUpdateAreaStatus = async (isActive) => {
    try {
      setLoading(true);
      let dataTemp = {
        isActive: isActive == "active" ? true : false,
        updatedAt: new Date(),
        updatedBy: currentUser?.id,
      };
      await Promise.all(selected.map((item) => updateArea(item, dataTemp)));
      await handleGetAllAreas();
      setSelected([]);
      showNotification("Area status updated successfully.");
      // Toast("success", "Area status updated successfully.");
    } catch (error) {
      Toast("error", error.message);
      console.log("error updating album", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteArea = async () => {
    try {
      setLoading(true);

      // Soft delete: keep the record but mark it inactive + deleted.
      await Promise.all(
        selected.map((item) => softDeleteArea(item, currentUser?.id))
      );

      // Re-order remaining (non-deleted) areas' positions
      const remainingAreas = filterNonDeletedAreas(allAreas).filter(
        (item) => !selected.includes(item.id)
      );
      await Promise.all(
        remainingAreas.map((item, index) =>
          updateArea(item.id, { position: index + 1 })
        )
      );
      await handleGetAllAreas();
      setSelected([]);
      showNotification("Area deleted successfully.");
      // Toast("success", "Area deleted successfully.");
    } catch (error) {
      Toast("error", error.message);
      console.log("error deleting quicktale", error.message);
    } finally {
      setLoading(false);
      handleDeleteModalClose();
    }
  };
  const handlePageLoadingClose = () => {
    setLoading(false);
  };
  return (
    <Box sx={{ display: "flex", width: "100%", paddingRight: 4 }}>
      <CssBaseline />
      <Header
        mainTitle={"Area Management"}
        bgColor="#fff"
        buttonText={
          currentUser?.role === "super" || currentUser?.role === "moderator"
            ? "Add Area"
            : undefined
        }
        handleSubmit={() => setAddAreaModal(true)}
      />
      <Sidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"}>
          <AreaTabsPreview areas={allAreas} />
          <FilterSection
            handleFilterModalOpen={handleFilterModalOpen}
            areaStatusFilterOptions={areaStatusFilterOptions}
            handleDeleteChip={handleDeleteChip}
          />
          <SearchBar
            selected={selected}
            setSelected={setSelected}
            handleSearch={handleSearchAreaData}
            handleUpdateAreaStatus={handleUpdateAreaStatus}
            handleDeleteArea={handleDeleteModalOpen}
            placeholder={"Search"}
          />
          <AreaManagementTable
            selected={selected}
            setSelected={setSelected}
            areaData={areaData}
            setAreaData={setAreaData}
            isLoading={areasLoading}
            showDeletedColumn={showDeleted}
            allowDrag={areaStatusFilterOptions.length === 0 && !searchActive}
          />
          <ConfirmationModal
            open={deleteModal}
            onClose={handleDeleteModalClose}
            onConfirm={handleDeleteArea}
            title={"Are you sure to delete the selected Area(s)?"}
          />
          <AddAreaModal
            open={addAreaModal}
            onClose={() => setAddAreaModal(false)}
            onSubmit={handleAddArea}
            allAreas={allAreas}
            loading={addingArea}
            defaultStatus={orgCreateAreaStatusDefault}
          />
          <FilterModalComponent
            modalOpen={filterModal}
            handleClose={handleFilterModalClose}
            resetFilter={resetFilter}
            handleFilter={handleFilterAreas}
            data={[
              {
                mainOptions: areaStatusFilterOptions,
                setMainOptions: setAreaStatusFilterOptions,
                type: "text",
                title: "Area Status",
                currentOptions: [
                  { label: "All", selected: true },
                  { label: "Active", selected: false },
                  { label: "Inactive", selected: false },
                ],
              },
            ]}
          />
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loading}
            onClick={handlePageLoadingClose}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AreaManagementContainer;
